/**
 * @file Recalls config-incomplete UI helpers.
 */

import { state } from "../../app/state/state.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";

const CONFIG_INCOMPLETE_TYPE = "config_incomplete";
const REQUIRED_STATUS_ROLE_KEYS = Object.freeze([
  "initial",
  "emailed",
  "booked",
  "rescheduled",
  "declined",
  "cancelled",
]);
const REQUIRED_REASON_LIST_KEYS = Object.freeze([
  "recallReasons",
  "declinedReasons",
  "cancelledReasons",
  "rescheduleReasons",
]);

/**
 * Returns the Recalls callable error type.
 * @param {object} error Callable error object.
 * @returns {string|null} Error type.
 */
export function getRecallsCallableErrorType(error) {
  return error?.details?.type || error?.customData?.type || null;
}

/**
 * Returns whether a callable error is a Recalls config-incomplete error.
 * @param {object} error Callable error object.
 * @returns {boolean} True when Recalls Settings are incomplete.
 */
export function isRecallsConfigIncompleteError(error) {
  return getRecallsCallableErrorType(error) === CONFIG_INCOMPLETE_TYPE;
}

function getMissingFields(error) {
  const missing = error?.details?.missing || error?.customData?.missing || [];
  return (Array.isArray(missing) ? missing : [missing])
    .map((entry) => {
      if (typeof entry === "string") {return entry;}
      return entry?.field || "";
    })
    .filter(Boolean);
}

function formatMissingField(field) {
  const labels = {
    "recalls.statusRoleMappings": "status role mappings",
    "recalls.recallReasons": "recall reasons",
    "recalls.declinedReasons": "declined reasons",
    "recalls.cancelledReasons": "cancelled reasons",
    "recalls.rescheduleReasons": "reschedule reasons",
    "recalls.nearDateWarningDays": "near-date warning days",
    "recalls.recentlyClosedRecallWarningDays": "recently closed warning days",
    "recalls.exportReplacementText": "export replacement text",
    "recalls.exportReplacementText.providers": "provider export text",
    "recalls.exportReplacementText.recallReasons": "reason export text",
    "users.isAppProvider": "at least one active app provider",
  };
  if (labels[field]) {return labels[field];}
  if (field.startsWith("recalls.statusRoleMappings.")) {
    return `${field.split(".").pop()} status mapping`;
  }
  return field.replace(/^recalls\./, "").replace(/\./g, " ");
}

function getConfigIncompleteBody(error) {
  const missingFields = getMissingFields(error);
  const missingHtml = missingFields.length > 0 ?
    `<ul class="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-600">
      ${missingFields.slice(0, 6).map((field) =>
        `<li>${escapeHTML(formatMissingField(field))}</li>`).join("")}
    </ul>` :
    "";
  const extraCount = Math.max(0, missingFields.length - 6);
  const extraHtml = extraCount > 0 ?
    `<p class="mt-2 text-sm text-gray-600">And ${extraCount} more.</p>` :
    "";

  if (state.currentUserProfile?.role === "admin") {
    return `
      <div class="text-sm text-gray-600">
        <p>Complete Recalls Settings before continuing.</p>
        ${missingHtml}
        ${extraHtml}
      </div>
    `;
  }

  return `
    <div class="text-sm text-gray-600">
      <p>Recalls Settings must be completed by an admin before this action can continue.</p>
      ${missingHtml}
      ${extraHtml}
    </div>
  `;
}

/**
 * Opens the Recalls config-incomplete modal.
 * @param {object} error Callable error object.
 * @returns {boolean} True when handled.
 */
export function openRecallsConfigIncompleteModal(error) {
  if (!isRecallsConfigIncompleteError(error)) {return false;}

  if (state.currentUserProfile?.role === "admin") {
    openConfirmationModal(
      "Complete Recalls Settings",
      getConfigIncompleteBody(error),
      async () => {
        state.settingsReturnContext = { tab: "recalls" };
        const { navigate } = await import("../../app/routing/navigationController.js");
        navigate("settings");
      },
      false,
      "Open Recalls Settings",
      null,
      "Opening...",
      "Cancel",
      { maxWidth: "sm", scrollBody: true },
    );
    return true;
  }

  openConfirmationModal(
    "Recalls Settings Incomplete",
    getConfigIncompleteBody(error),
    async () => {},
    false,
    "OK",
    null,
    "OK",
    "Cancel",
    { maxWidth: "sm", scrollBody: true },
  );
  return true;
}

function hasActiveOptions(options = []) {
  return (Array.isArray(options) ? options : [])
    .some((option) => option?.id && option.isActive !== false);
}

/**
 * Returns locally detectable Recalls configuration issues.
 * @param {object} config App config.
 * @returns {Array<object>} Missing or invalid config entries.
 */
export function getLocalRecallsConfigIssues(config = {}) {
  const issues = [];
  const recallsConfig = config.recalls || {};
  const statuses = Array.isArray(config.recallStatus) ? config.recallStatus : [];
  const statusIds = new Set(statuses
    .filter((status) => status?.id)
    .map((status) => String(status.id)));
  const mappedStatusIds = [];

  REQUIRED_STATUS_ROLE_KEYS.forEach((role) => {
    const statusId = String(recallsConfig.statusRoleMappings?.[role] || "").trim();
    if (!statusId) {
      issues.push({ field: `recalls.statusRoleMappings.${role}` });
      return;
    }
    if (!statusIds.has(statusId)) {
      issues.push({
        field: `recalls.statusRoleMappings.${role}`,
        reason: "missing_status",
      });
      return;
    }
    mappedStatusIds.push(statusId);
  });

  if (new Set(mappedStatusIds).size !== mappedStatusIds.length) {
    issues.push({
      field: "recalls.statusRoleMappings",
      reason: "duplicate_status",
    });
  }

  REQUIRED_REASON_LIST_KEYS.forEach((key) => {
    if (!hasActiveOptions(recallsConfig[key])) {
      issues.push({ field: `recalls.${key}` });
    }
  });

  [
    "nearDateWarningDays",
    "recentlyClosedRecallWarningDays",
  ].forEach((key) => {
    const threshold = recallsConfig[key];
    if (
      threshold !== null &&
      threshold !== undefined &&
      threshold !== "" &&
      (!Number.isInteger(Number(threshold)) || Number(threshold) < 0)
    ) {
      issues.push({
        field: `recalls.${key}`,
        reason: "invalid_threshold",
      });
    }
  });

  const replacementText = recallsConfig.exportReplacementText;
  if (!replacementText || typeof replacementText !== "object") {
    issues.push({ field: "recalls.exportReplacementText" });
  } else {
    if (!replacementText.providers || typeof replacementText.providers !== "object") {
      issues.push({ field: "recalls.exportReplacementText.providers" });
    }
    if (
      !replacementText.recallReasons ||
      typeof replacementText.recallReasons !== "object"
    ) {
      issues.push({ field: "recalls.exportReplacementText.recallReasons" });
    }
  }

  return issues;
}

/**
 * Renders a route-level Recalls configuration notice.
 * @param {Array<object>} issues Locally detectable config issues.
 * @returns {string} Notice HTML.
 */
export function renderRecallsConfigIncompleteNotice(issues = []) {
  if (!issues.length) {return "";}
  const isAdmin = state.currentUserProfile?.role === "admin";
  const issueLabels = issues.slice(0, 4)
    .map((issue) => formatMissingField(issue.field))
    .join(", ");

  return `
    <div class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-recalls-config-incomplete-notice>
      <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div class="font-semibold">Recalls Settings incomplete</div>
          <div class="mt-1 text-amber-800">
            ${escapeHTML(issueLabels || "Complete required Recalls Settings before workflow actions.")}
          </div>
        </div>
        ${isAdmin ? `
          <button type="button" class="settings-primary-action-btn self-start md:self-auto" data-recalls-open-settings>
            Open Recalls Settings
          </button>
        ` : ""}
      </div>
    </div>
  `;
}
